import { useState } from 'react';
import {
  Truck,
  Star,
  ChevronDown,
  Plus,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type {
  ItemSupplier, CostHistoryEntry 
} from '../../../types';
import { AddSupplierModal } from '../AddSupplierModal';
import { CostTrendChart } from './CostTrendChart';

interface SuppliersTabProps {
  readonly suppliers?: ItemSupplier[];
  readonly costHistory?: CostHistoryEntry[];
  readonly onAddSupplier?: (supplier: Omit<ItemSupplier, 'supplierId'>) => void;
}

export function SuppliersTab({
  suppliers, costHistory, onAddSupplier 
}: SuppliersTabProps) {
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAddSupplier = (supplier: Omit<ItemSupplier, 'supplierId'>) => {
    if (onAddSupplier) {
      onAddSupplier(supplier);
    } else {
      toast.success(`Added supplier: ${supplier.supplierName}`);
    }
    setShowAddModal(false);
  };

  if (!suppliers?.length) {
    return (
      <>
        <AddSupplierModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddSupplier}
        />
        <EmptyState
          icon={Truck}
          title="No suppliers configured"
          description="Add suppliers to track pricing and manage sourcing for this item."
          actionLabel="Add Supplier"
          onAction={() => setShowAddModal(true)}
        />
      </>
    );
  }

  const toggleExpand = (supplierId: string) => {
    setExpandedSupplier(expandedSupplier === supplierId ? null : supplierId);
  };

  return (
    <div className="space-y-6">
      <AddSupplierModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddSupplier}
      />

      {/* Cost Trend Chart */}
      {costHistory && costHistory.length > 0 && (
        <CostTrendChart costHistory={costHistory} />
      )}

      {/* Suppliers List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900">Active Suppliers ({suppliers.length})</h3>
          <Button variant="outline" size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Supplier
          </Button>
        </div>
        <div className="space-y-3">
          {suppliers.map((supplier) => (
            <SupplierCard
              key={supplier.supplierId}
              supplier={supplier}
              isExpanded={expandedSupplier === supplier.supplierId}
              onToggle={() => toggleExpand(supplier.supplierId)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
interface SupplierCardProps {
  readonly supplier: ItemSupplier;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}

function SupplierCard({
  supplier, isExpanded, onToggle 
}: SupplierCardProps) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-3">
          {supplier.isPreferred && (
            <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
          )}
          <div>
            <p className="font-medium text-gray-900">{supplier.supplierName}</p>
            <SupplierSummary supplier={supplier} />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-lg font-semibold text-gray-900">
              {supplier.currency} {supplier.unitPrice.toFixed(2)}
            </p>
            {supplier.casePrice && (
              <p className="text-xs text-gray-500">
                Case: {supplier.currency} {supplier.casePrice.toFixed(2)}
              </p>
            )}
          </div>
          <ChevronDown className={cn(
            'h-5 w-5 text-gray-400 transition-transform',
            isExpanded && 'rotate-180'
          )} />
        </div>
      </button>
      {isExpanded && <SupplierDetails supplier={supplier} />}
    </div>
  );
}

function SupplierSummary({ supplier }: { readonly supplier: ItemSupplier }) {
  const parts: string[] = [];
  parts.push(`Lead: ${supplier.leadTimeDays} days`);
  parts.push(`MOQ: ${supplier.minOrderQuantity}`);
  
  if (supplier.packaging) {
    const packagingStr = formatPackaging(supplier.packaging);
    parts.push(packagingStr);
  }

  return (
    <div className="flex items-center gap-3 text-sm text-gray-500">
      {parts.map((part, idx) => (
        <span key={part}>
          {idx > 0 && <span className="mr-3">•</span>}
          {part}
        </span>
      ))}
    </div>
  );
}

function formatPackaging(packaging: ItemSupplier['packaging']): string {
  if (!packaging) return '';
  const base = `${packaging.unitSize}${packaging.unitOfMeasure}`;
  if (packaging.unitsPerCase) {
    return `${base} × ${packaging.unitsPerCase}`;
  }
  return base;
}

function SupplierDetails({ supplier }: { readonly supplier: ItemSupplier }) {
  return (
    <div className="border-t bg-gray-50 p-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <DetailSection title="Product Codes">
          {supplier.supplierSku && <CodeRow label="Supplier SKU" value={supplier.supplierSku} />}
          {supplier.ean && <CodeRow label="EAN" value={supplier.ean} />}
          {supplier.upc && <CodeRow label="UPC" value={supplier.upc} />}
          {supplier.dun14 && <CodeRow label="DUN-14" value={supplier.dun14} />}
          {supplier.gtin && <CodeRow label="GTIN" value={supplier.gtin} />}
          {!supplier.supplierSku && !supplier.ean && !supplier.upc && !supplier.dun14 && !supplier.gtin && (
            <p className="text-gray-400 italic">No codes configured</p>
          )}
        </DetailSection>

        <DetailSection title="Packaging Format">
          {supplier.packaging ? (
            <PackagingDetails packaging={supplier.packaging} />
          ) : (
            <p className="text-gray-400 italic">No packaging info</p>
          )}
        </DetailSection>

        <DetailSection title="Order Constraints">
          <CodeRow label="Lead Time" value={`${supplier.leadTimeDays} days`} />
          <CodeRow label="Min Order Qty" value={supplier.minOrderQuantity.toString()} />
          {supplier.orderMultiple && (
            <CodeRow label="Order Multiple" value={supplier.orderMultiple.toString()} />
          )}
          {supplier.maxOrderQuantity && (
            <CodeRow label="Max Order Qty" value={supplier.maxOrderQuantity.toString()} />
          )}
        </DetailSection>

        {supplier.lastPurchaseDate && (
          <LastPurchaseSection supplier={supplier} />
        )}

        {supplier.supplierProductName && (
          <div className="space-y-2 md:col-span-2">
            <h4 className="text-xs font-medium text-gray-500 uppercase">Supplier Product Name</h4>
            <div className="rounded-lg bg-white p-3 text-sm">
              <p className="text-gray-900">{supplier.supplierProductName}</p>
            </div>
          </div>
        )}

        {supplier.notes && (
          <div className="space-y-2 md:col-span-2 lg:col-span-3">
            <h4 className="text-xs font-medium text-gray-500 uppercase">Notes</h4>
            <div className="rounded-lg bg-white p-3 text-sm">
              <p className="text-gray-600">{supplier.notes}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PackagingDetails({ packaging }: { readonly packaging: NonNullable<ItemSupplier['packaging']> }) {
  return (
    <>
      <CodeRow label="Unit" value={`${packaging.unitSize} ${packaging.unitOfMeasure}`} />
      {packaging.unitsPerCase && <CodeRow label="Units/Case" value={packaging.unitsPerCase.toString()} />}
      {packaging.casesPerPallet && <CodeRow label="Cases/Pallet" value={packaging.casesPerPallet.toString()} />}
      {packaging.description && <p className="text-gray-600 mt-2">{packaging.description}</p>}
    </>
  );
}

function LastPurchaseSection({ supplier }: { readonly supplier: ItemSupplier }) {
  return (
    <DetailSection title="Last Purchase">
      <CodeRow
        label="Date"
        value={new Date(supplier.lastPurchaseDate ?? '').toLocaleDateString()}
      />
      {supplier.lastPurchasePrice && (
        <CodeRow
          label="Price"
          value={`${supplier.currency} ${supplier.lastPurchasePrice.toFixed(2)}`}
        />
      )}
      {supplier.lastPurchaseQuantity && (
        <CodeRow label="Quantity" value={supplier.lastPurchaseQuantity.toString()} />
      )}
    </DetailSection>
  );
}

interface DetailSectionProps {
  readonly title: string;
  readonly children: React.ReactNode;
}

function DetailSection({
  title, children 
}: DetailSectionProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-gray-500 uppercase">{title}</h4>
      <div className="rounded-lg bg-white p-3 space-y-2 text-sm">
        {children}
      </div>
    </div>
  );
}

interface CodeRowProps {
  readonly label: string;
  readonly value: string;
}

function CodeRow({
  label, value 
}: CodeRowProps) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="font-mono text-gray-900">{value}</span>
    </div>
  );
}

interface EmptyStateProps {
  readonly icon: typeof Package;
  readonly title: string;
  readonly description: string;
  readonly actionLabel?: string;
  readonly onAction?: () => void;
}

function EmptyState({
  icon: emptyIcon,
  title,
  description,
  actionLabel,
  onAction,
}: EmptyStateProps) {
  const IconComponent = emptyIcon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-gray-100 p-4 mb-4">
        <IconComponent className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-sm">{description}</p>
      {actionLabel && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onAction}>
          <Plus className="mr-2 h-4 w-4" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
